import type { FC, PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useState } from 'react';

import type { ProfileDocumentType } from '@xing-com/crate-common-graphql-types';
import { useFileServerUpload } from '@xing-com/crate-jobs-domain-apply-uploads';

import type { FileUpload } from './types';

type UploadsByFieldId = Record<string, FileUpload | undefined>;

type FileUploadContextType = {
  uploadsByFieldId: UploadsByFieldId;
  addFileUpload: (fieldId: string, file: File) => Promise<void>;
};

const FileUploadContext = createContext<FileUploadContextType | undefined>(
  undefined
);

type Props = PropsWithChildren<{
  profileId?: string;
  profileDocumentTypesByFieldId?: Record<string, ProfileDocumentType>;
}>;

export const FileUploadProvider: FC<Props> = ({
  children,
  profileId,
  profileDocumentTypesByFieldId,
}) => {
  const { deleteFile, deleteProfileDocument, uploadFile } =
    useFileServerUpload();
  const [uploadsByFieldId, setUploadsByFieldId] = useState<UploadsByFieldId>(
    {}
  );
  const addFileUpload = useCallback(
    async (fieldId: string, file: File): Promise<void> => {
      const abortController = new AbortController();
      const fileDescriptor = {
        name: file.name,
        size: file.size,
        type: file.type,
      };
      let requestedFileServerId: FileUpload['fileServerId'];
      let requestedAuthToken: FileUpload['authToken'];
      let profileDocumentId: string | undefined;
      const documentType = profileDocumentTypesByFieldId?.[fieldId];
      const profileDocument =
        profileId && documentType ? { profileId, documentType } : undefined;
      const cancelUpload = (): void => {
        abortController.abort();
        if (profileDocumentId && profileId) {
          void deleteProfileDocument({
            profileId,
            documentId: profileDocumentId,
          });
        }
        if (requestedFileServerId && requestedAuthToken) {
          void deleteFile({
            id: requestedFileServerId,
            authToken: requestedAuthToken,
          });
        }
        setUploadsByFieldId((previousState) => {
          const { [fieldId]: _, ...rest } = previousState;
          return rest;
        });
      };
      setUploadsByFieldId((previousState) => ({
        ...previousState,
        [fieldId]: {
          file: fileDescriptor,
          progress: 0,
          status: 'loading-token',
          cancelUpload,
        },
      }));

      try {
        const {
          upload: { id: fileServerId, authToken },
          status,
          error: uploadError,
          profileDocumentId: createdProfileDocumentId,
        } = await uploadFile(file, {
          signal: abortController.signal,
          profileDocument,
          onUploadRequested: ({ id: fileServerId, authToken }) => {
            requestedFileServerId = fileServerId;
            requestedAuthToken = authToken;
            setUploadsByFieldId((previousState) => ({
              ...previousState,
              [fieldId]: {
                file: fileDescriptor,
                fileServerId,
                stagedUploadId: profileDocument ? fileServerId : undefined,
                authToken,
                status: 'uploading',
                progress: 0,
                cancelUpload,
              },
            }));
          },
          onUploadProgress: (progress, { id: fileServerId, authToken }) => {
            setUploadsByFieldId((previousState) => ({
              ...previousState,
              [fieldId]: {
                file: fileDescriptor,
                fileServerId,
                stagedUploadId: profileDocument ? fileServerId : undefined,
                authToken,
                status: 'uploading',
                progress,
                cancelUpload,
              },
            }));
          },
        });

        if (
          status === 204 &&
          !uploadError &&
          (!profileDocument || createdProfileDocumentId) &&
          !abortController.signal.aborted
        ) {
          profileDocumentId = createdProfileDocumentId;
          setUploadsByFieldId((previousState) => ({
            ...previousState,
            [fieldId]: {
              file: fileDescriptor,
              fileServerId: createdProfileDocumentId ?? fileServerId,
              stagedUploadId: createdProfileDocumentId
                ? fileServerId
                : undefined,
              authToken,
              status: 'success',
              progress: 100,
              cancelUpload,
            },
          }));
        } else if (!abortController.signal.aborted) {
          setUploadsByFieldId((previousState) => ({
            ...previousState,
            [fieldId]: {
              file: fileDescriptor,
              fileServerId,
              authToken,
              error: uploadError,
              status: 'error',
              progress: 0,
              cancelUpload,
            },
          }));
        }
        // oxlint-disable-next-line typescript/no-unused-vars
      } catch (error) {
        // update file data and set status = error
        if (!abortController.signal.aborted) {
          setUploadsByFieldId((previousState) => ({
            ...previousState,
            [fieldId]: {
              file: fileDescriptor,
              status: 'error',
              progress: 0,
              cancelUpload,
            },
          }));
        }
      }
    },
    [
      deleteFile,
      deleteProfileDocument,
      profileDocumentTypesByFieldId,
      profileId,
      uploadFile,
    ]
  );

  return (
    <FileUploadContext.Provider value={{ uploadsByFieldId, addFileUpload }}>
      {children}
    </FileUploadContext.Provider>
  );
};

export const useFileUpload = (fieldId: string): FileUpload | undefined => {
  const context = useContext(FileUploadContext);

  if (!context) {
    throw new Error('useFileUpload must be used within an FileUploadProvider');
  }

  return context.uploadsByFieldId[fieldId];
};

export const useAddFileUpload: (fieldId: string) => (file: File) => void = (
  fieldId
) => {
  const context = useContext(FileUploadContext);

  if (!context) {
    throw new Error(
      'useAddFileUpload must be used within an FileUploadProvider'
    );
  }

  const { addFileUpload } = context;

  const addFileWithFieldId = useCallback(
    (file: File) => {
      addFileUpload(fieldId, file);
    },
    [addFileUpload, fieldId]
  );

  return addFileWithFieldId;
};
